import * as Sentry from '@sentry/react';

import { DEFAULT_API_BASE_URL, normalizeApiBaseUrl } from './defaultApiUrl.js';

// Jest can't parse `import.meta.env` reliably, so we read from a global set by bootstrapApiUrl.js.
// Resolved at call time so the URL is never frozen before the bootstrap module runs.
export function getApiBaseUrl() {
  return normalizeApiBaseUrl(globalThis.__API_BASE_URL__ || DEFAULT_API_BASE_URL)
}

// Report unexpected API failures (network down, 5xx) to Sentry Issues + Logs.
// Expected/handled responses (auth, validation, not-found) are intentionally skipped.
function reportApiError(error, { method, path, status, detail } = {}) {
  const httpStatus = status ?? 'network'
  const extra = {
    method: method || 'GET',
    path,
    http_status: httpStatus,
    detail: detail || error.message,
  }
  Sentry.captureException(error, {
    tags: { area: 'api', http_status: String(httpStatus) },
    extra,
    contexts: { request: extra },
  })
  if (Sentry.logger && typeof Sentry.logger.error === 'function') {
    Sentry.logger.error(error.message, extra)
  }
}

// Global logout handler - will be set by AuthContext
let logoutHandler = null;

export function setLogoutHandler(handler) {
  logoutHandler = handler;
}

function getAuthHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Turn FastAPI / API error payloads into a user-readable string. */
export function formatApiError(data, fallback = 'Request failed') {
  if (!data) return fallback;

  const detail = data.detail ?? data.message;
  if (typeof detail === 'string' && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const loc = Array.isArray(item.loc)
            ? item.loc.filter((p) => p !== 'body').join('.')
            : '';
          const msg = item.msg || item.message;
          if (msg && loc) return `${loc}: ${msg}`;
          if (msg) return msg;
        }
        return null;
      })
      .filter(Boolean);
    if (parts.length) return parts.join(' ');
  }

  if (detail && typeof detail === 'object') {
    const msg = detail.msg || detail.message;
    if (typeof msg === 'string') return msg;
  }

  return fallback;
}

async function request(path, options = {}) {
  const url = `${getApiBaseUrl()}${path}`;
  const isAuthRequest =
    path.startsWith('/api/auth/login') ||
    path.startsWith('/api/auth/signup') ||
    path.startsWith('/api/auth/forgot-password') ||
    path.startsWith('/api/auth/reset-password');
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...(options.headers || {}),
  };

  const method = options.method || 'GET';

  let resp;
  try {
    resp = await fetch(url, { ...options, headers });
  } catch (networkError) {
    // Never surface fetch internals or the API host to users.
    const error = new Error('Please check your internet connection and try again.');
    error.code = 'NETWORK_ERROR';
    reportApiError(error, { method, path, detail: networkError.message });
    throw error;
  }
  
  const isJson = resp.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await resp.json().catch(() => ({})) : null;

  if (!resp.ok) {
    // Handle 401 Unauthorized - token expired or invalid
    if (resp.status === 401) {
      // For login/signup, surface backend auth error (e.g. wrong credentials)
      if (isAuthRequest) {
        throw new Error(formatApiError(data, 'Authentication failed.'));
      }
      // Clear token from localStorage
      localStorage.removeItem('access_token');
      // Call logout handler if set (will clear user state and redirect)
      if (logoutHandler) {
        logoutHandler();
      }
      // Throw error with specific message
      throw new Error('Session expired. Please log in again.');
    }
    // Handle 403 Forbidden - admin access required
    if (resp.status === 403) {
      throw new Error('Access denied. Admin privileges required.');
    }
    // Handle 404 Not Found
    if (resp.status === 404) {
      const error = new Error('Something went wrong. Please try again.');
      error.code = 'NOT_FOUND';
      throw error;
    }
    const detail = formatApiError(data, resp.statusText || `Server error (${resp.status})`)
    const error = new Error(detail)
    // Only report genuine server-side failures (5xx); 4xx are expected/handled.
    if (resp.status >= 500) {
      reportApiError(error, { method, path, status: resp.status, detail })
    }
    throw error;
  }
  return data;
}

export const api = {
  // Auth
  signup: (payload) => request('/api/auth/signup', { method: 'POST', body: JSON.stringify(payload) }),
  login:  (payload) => request('/api/auth/login',  { method: 'POST', body: JSON.stringify(payload) }),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  forgotPassword: (payload) =>
    request('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify(payload) }),
  resetPassword: (payload) =>
    request('/api/auth/reset-password', { method: 'POST', body: JSON.stringify(payload) }),

  // Users
  me: () => request('/api/users/me'),
  updateMe: (payload) => request('/api/users/me', { method: 'PUT', body: JSON.stringify(payload) }),
  profile: () => request('/api/users/me/profile'),
  updateProfile: (payload) => request('/api/users/me/profile', { method: 'PUT', body: JSON.stringify(payload) }),

  // Skills & Lessons
  skills: () => request('/api/skills/'),
  skill: (id) => request(`/api/skills/${id}`),
  skillLessons: (id, difficulty) => request(`/api/skills/${id}/lessons${difficulty ? `?difficulty=${encodeURIComponent(difficulty)}` : ''}`),
  lesson: (lessonId) => request(`/api/skills/lessons/${lessonId}`),

  // Progress / Dashboard
  createOrUpdateProgress: (payload) => request('/api/progress/', { method: 'POST', body: JSON.stringify(payload) }),
  getProgress: () => request('/api/progress/'),
  getLessonProgress: (lessonId) => request(`/api/progress/lesson/${lessonId}`),
  updateLessonProgress: (lessonId, payload) => request(`/api/progress/lesson/${lessonId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  dashboard: () => {
    const tz = new Date().getTimezoneOffset()
    return request(`/api/progress/dashboard?tz_offset_minutes=${tz}`)
  },

  // Activities
  activities: (limit = 20, activityType, offset = 0) =>
    request(
      `/api/activities/?limit=${limit}&offset=${offset}${
        activityType ? `&activity_type=${encodeURIComponent(activityType)}` : ""
      }`
    ),
  activityDailySummary: (days = 7) => {
    const tz = new Date().getTimezoneOffset()
    return request(`/api/activities/daily-summary?days=${days}&tz_offset_minutes=${tz}`)
  },
  activityStats: () => {
    const tz = new Date().getTimezoneOffset()
    return request(`/api/activities/stats?tz_offset_minutes=${tz}`, { cache: "no-store" })
  },

  // Reading
  listReadingExercises: (difficulty) => {
    const qs = difficulty ? `?difficulty=${encodeURIComponent(difficulty)}` : ''
    return request(`/api/reading/exercises${qs}`)
  },
  readingExercise: ({ difficulty, exerciseId, title } = {}) => {
    const params = new URLSearchParams()
    if (difficulty) params.set('difficulty', difficulty)
    if (exerciseId) params.set('exercise_id', exerciseId)
    if (title) params.set('title', title)
    const qs = params.toString()
    return request(`/api/reading/exercise${qs ? `?${qs}` : ''}`)
  },
  submitReading: (payload) => request('/api/reading/submit', { method: 'POST', body: JSON.stringify(payload) }),
  getReadingAttempts: (lessonId) => request(`/api/reading/attempts/${lessonId}`),

  // Writing
  listWritingPrompts: (difficulty) => {
    const qs = difficulty ? `?difficulty=${encodeURIComponent(difficulty)}` : ''
    return request(`/api/writing/prompts${qs}`)
  },
  writingPrompt: ({ difficulty, title } = {}) => {
    const params = new URLSearchParams()
    if (difficulty) params.set('difficulty', difficulty)
    if (title) params.set('title', title)
    const qs = params.toString()
    return request(`/api/writing/prompt${qs ? `?${qs}` : ''}`)
  },
  submitWriting: (payload) => request('/api/writing/submit', { method: 'POST', body: JSON.stringify(payload) }),
  writingAiFeedback: (payload) =>
    request('/api/writing/ai-feedback', { method: 'POST', body: JSON.stringify(payload) }),
  getWritingAttempts: (lessonId) => request(`/api/writing/attempts/${lessonId}`),

  // Listening
  listListeningExercises: (difficulty) => {
    const qs = difficulty ? `?difficulty=${encodeURIComponent(difficulty)}` : ''
    return request(`/api/listening/exercises${qs}`)
  },
  listeningExercise: ({ difficulty, exerciseId, title } = {}) => {
    const params = new URLSearchParams()
    if (difficulty) params.set('difficulty', difficulty)
    if (exerciseId) params.set('exercise_id', exerciseId)
    if (title) params.set('title', title)
    const qs = params.toString()
    return request(`/api/listening/exercise${qs ? `?${qs}` : ''}`)
  },
  submitListening: (payload) => request('/api/listening/submit', { method: 'POST', body: JSON.stringify(payload) }),

  // Speaking
  speakingPrompt: () => request('/api/speaking/prompt'),
  submitSpeaking: (payload) => request('/api/speaking/submit', { method: 'POST', body: JSON.stringify(payload) }),
  
  // Speaking Conversations
  listSpeakingExercises: (level) => {
    const qs = level != null && level !== '' ? `?level=${encodeURIComponent(level)}` : ''
    return request(`/api/speaking/exercises${qs}`)
  },
  getConversationLevels: () => request('/api/speaking/conversations'),
  getConversationsByLevel: (level) => request(`/api/speaking/conversations/level/${level}`),
  getConversation: (conversationId) => request(`/api/speaking/conversations/${conversationId}`),
  startSpeakingConversation: (conversationId) =>
    request('/api/speaking/conversation/start', {
      method: 'POST',
      body: JSON.stringify({ conversation_id: conversationId }),
    }),
  sendSpeakingTurn: (payload) =>
    request('/api/speaking/conversation/turn', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  endSpeakingConversation: (payload) =>
    request('/api/speaking/conversation/end', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Vocabulary
  saveVocabulary: (payload) =>
    request('/api/vocabulary/save', { method: 'POST', body: JSON.stringify(payload) }),
  listVocabulary: (params = {}) => {
    const search = new URLSearchParams()
    if (params.skill) search.set('skill', params.skill)
    if (params.status) search.set('status', params.status)
    if (params.lessonId) search.set('lesson_id', String(params.lessonId))
    if (params.dueOnly) search.set('due_only', 'true')
    if (params.q) search.set('q', params.q)
    const qs = search.toString()
    return request(`/api/vocabulary/${qs ? `?${qs}` : ''}`)
  },
  dueVocabulary: () => request('/api/vocabulary/due'),
  vocabularyStats: () => {
    const tz = new Date().getTimezoneOffset()
    return request(`/api/vocabulary/stats?tz_offset_minutes=${tz}`)
  },
  vocabularyQuiz: (count = 5) => request(`/api/vocabulary/quiz?count=${count}`),
  completeVocabularyQuiz: (payload) =>
    request('/api/vocabulary/quiz/complete', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  reviewVocabulary: (id, rating) =>
    request(`/api/vocabulary/${id}/review`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    }),
  updateVocabulary: (id, payload) =>
    request(`/api/vocabulary/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  markVocabularyLearned: (id) =>
    request(`/api/vocabulary/${id}/learned`, { method: 'POST' }),
  markVocabularyLearning: (id) =>
    request(`/api/vocabulary/${id}/learning`, { method: 'POST' }),
  async markVocabularyStatus(id, status) {
    const learned = status === 'mastered'
    try {
      return await (learned ? this.markVocabularyLearned(id) : this.markVocabularyLearning(id))
    } catch (error) {
      const missing = error.code === 'NOT_FOUND' || /endpoint not found/i.test(error.message || '')
      if (!missing) throw error
      return this.updateVocabulary(id, { status })
    }
  },
  deleteVocabulary: (id) => request(`/api/vocabulary/${id}`, { method: 'DELETE' }),
  grammarTopics: () => request('/api/grammar/topics'),
  grammarTopic: (id) => request(`/api/grammar/topics/${id}`),
  completeGrammarTopic: (id, answers) =>
    request(`/api/grammar/topics/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ answers }),
    }),

  // Billing / Subscription
  billingStatus: () => request('/api/billing/status'),
  createCheckout: () => request('/api/billing/checkout', { method: 'POST' }),
  billingPortal: () => request('/api/billing/portal', { method: 'POST' }),

  // Tutor Chat
  chatStatus: () => request('/api/chat/status'),
  sendChatMessage: (payload) =>
    request('/api/chat/message', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Admin
  adminUsers: () => request('/api/users/'),

  // Feedback
  submitFeedback: (payload) => request('/api/feedback/', { method: 'POST', body: JSON.stringify(payload) }),
  // Removed getMyFeedback - only admins can view feedback
  getAllFeedback: (status, type) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (type) params.append('feedback_type', type);
    return request(`/api/feedback/all${params.toString() ? '?' + params.toString() : ''}`);
  },
  getFeedbackStats: () => request('/api/feedback/stats'),
  updateFeedbackStatus: (id, status, notes) => {
    const params = new URLSearchParams({ new_status: status });
    if (notes) params.append('admin_notes', notes);
    return request(`/api/feedback/${id}/status?${params.toString()}`, { method: 'PUT' });
  },
  
  // Helper for generic GET requests
  get: (path) => request(path),
  put: (path, options = {}) => request(path, { method: 'PUT', ...options }),
};
